/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';

export interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
}

export interface Serializer<T> {
  read: (raw: string) => T;
  write: (value: T) => string;
}

const storageSerializers: Record<'boolean' | 'object' | 'number' | 'any' | 'string' | 'map' | 'set' | 'date', Serializer<any>> = {
  boolean: {
    read: (v: any) => v === 'true',
    write: (v: any) => String(v),
  },
  object: {
    read: (v: any) => JSON.parse(v),
    write: (v: any) => JSON.stringify(v),
  },
  number: {
    read: (v: any) => Number.parseFloat(v),
    write: (v: any) => String(v),
  },
  any: {
    read: (v: any) => v,
    write: (v: any) => String(v),
  },
  string: {
    read: (v: any) => v,
    write: (v: any) => String(v),
  },
  map: {
    read: (v: any) => new Map(JSON.parse(v)),
    write: (v: any) => JSON.stringify(Array.from((v as Map<any, any>).entries())),
  },
  set: {
    read: (v: any) => new Set(JSON.parse(v)),
    write: (v: any) => JSON.stringify(Array.from(v as Set<any>)),
  },
  date: {
    read: (v: any) => new Date(v),
    write: (v: any) => v.toISOString(),
  },
};

function guessSerializerType<T extends (string | number | boolean | object | null)>(rawInit: T) {
  return rawInit == null
    ? 'any'
    : rawInit instanceof Set
      ? 'set'
      : rawInit instanceof Map
        ? 'map'
        : rawInit instanceof Date
          ? 'date'
          : typeof rawInit === 'boolean'
            ? 'boolean'
            : typeof rawInit === 'string'
              ? 'string'
              : typeof rawInit === 'object'
                ? 'object'
                : !Number.isNaN(rawInit)
                    ? 'number'
                    : 'any';
}

@Injectable({
  providedIn: 'root',
})
export class Storage {
  set<T extends (string | number | boolean | object | null)>(key: string, data: T, storage: StorageLike = localStorage) {
    if (data === null) {
      storage.removeItem(key);
      return;
    }

    const type = guessSerializerType(data);
    storage.setItem(key, storageSerializers[type].write(data));
  }

  get<T extends (string | number | boolean | object | null)>(key: string, type: 'boolean' | 'object' | 'number' | 'any' | 'string' | 'map' | 'set' | 'date', storage: StorageLike = localStorage): T | null {
    const raw = storage.getItem(key);

    if (raw === null) {
      return raw;
    }

    const serializer = storageSerializers[type];

    try {
      return serializer.read(raw);
    }
    catch (error) {
      console.error(`Storage failed to read data for "${key}". Returning null.`, error);
      return null;
    }
  }

  remove(key: string, storage: StorageLike = localStorage) {
    storage.removeItem(key);
  }

  clear(storage: StorageLike = localStorage) {
    storage.clear();
  }
}
